/**
 * How every writing route reaches the one composer (F-JR-19, F-JR-20, F-JR-40).
 *
 * F-JR-19 is a *singleton* contract: one shared full-screen presentation, bound
 * to one `activeComposer`, attached once above the Journal and workout-summary
 * routes. That only holds if there is exactly one place that can open it, which
 * is what this session is. Journal home, the reader, the goals workspace and
 * the summary's recap handoff all call the same five `begin…` methods and get
 * back the same boolean.
 *
 * The boolean is F-JR-20. Every route is gated on a verified-ready library, and
 * `false` is how a refusal reaches the surface that has to print the reason —
 * which it does from `refusal`, so "still opening" is never printed over
 * "unavailable". Telling a user to try again in a moment when the library
 * cannot be opened at all is an instruction that will never come true.
 *
 * The repository arrives as a prop rather than from a context of its own, for
 * the same reason the reader's presentation takes one: the storage ports are not
 * part of the app's `PortBundle`, and the host that read the library already
 * holds one. A second repository constructed here would be a second connection
 * disagreeing with the first.
 */

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type {
  JournalComposerSeed,
  JournalDraft,
  JournalEntry,
  JournalGoal,
  JournalOpenLibrary,
  JournalPromptReference,
  JournalRecapRequest,
} from "@ff/domain";
import {
  JOURNAL_GOAL_SCHEMA_VERSION,
  JOURNAL_FIRST_REVISION,
  JOURNAL_LIBRARY_COPY,
  JOURNAL_PROMPT_FAMILY_KIND,
  journalGoalLink,
  journalInstantFromMilliseconds,
} from "@ff/domain";
import type { JournalRepositoryPort } from "@ff/domain/ports";
import { canonicalJson } from "@ff/store";

import { useNavigation } from "../../../app-state/navigation";
import { fullViewportEntry } from "../../../shell/routeTable";
import { useOptionalAnnouncer, usePorts } from "../../../app-state/ports";
import { usePrivacySession } from "../../../privacy";
import type { JournalComposerDependencies } from "./controller";
import { JournalComposerController } from "./controller";
import type { NewGoalDraft } from "./ContextSheet";
import type { JournalComposerIdentity } from "./seeds";
import {
  blankComposerSeed,
  editingComposerSeed,
  journalComposerGate,
  mergingComposerSeed,
  recapRefusalMessage,
  resolveJournalRecap,
  resumingComposerSeed,
  workoutRecapComposerSeed,
} from "./seeds";

/** The route's path in `shell/routeTable.ts`. Presented, never navigated to. */
export const JOURNAL_COMPOSER_ROUTE_PATH = "/journal/compose";

/**
 * Resolved at module scope and **by name**, the way the shell resolves Ringside
 * and Settings resolves the drill library. `fullViewportEntry` throws for a path
 * the table no longer declares, so a renamed surface fails at import with the
 * path it wanted — instead of `presentFullViewport` setting a path the shell has
 * no component for and the surface silently never appearing.
 */
const PRESENTED_PATH = fullViewportEntry(JOURNAL_COMPOSER_ROUTE_PATH).path;

/** What F-JR-02, F-JR-06 and F-JR-40 hand the composer. */
export interface JournalWritingRequest {
  /** The prompt's family also decides the entry's kind. */
  readonly prompt?: JournalPromptReference;
  readonly goal?: JournalGoal;
}

export interface JournalComposerSessionState {
  readonly controller: JournalComposerController | null;
  /**
   * The id factory the composition root supplied. Re-published here so the
   * route does not need a second context for it — and so there is exactly one
   * source of entry, block and goal ids for a given session.
   */
  readonly identity: JournalComposerIdentity;
  /** Every goal the Context sheet can link, from the revealed library only. */
  readonly goals: readonly JournalGoal[];
  /**
   * F-JR-31's inline goal creation. `null` is a refused write, which the sheet
   * reports without touching the entry — a link pointing at a goal that was
   * never saved is worse than no link.
   */
  createGoal(draft: NewGoalDraft): Promise<JournalGoal | null>;
  /**
   * The sentence the last refusal produced, or null. Kept rather than thrown so
   * a caller that only needs "did it open" can ignore it, while the surface that
   * has to say *why* reads the exact sentence the gate chose.
   */
  readonly refusal: string | null;
  beginWriting(request: JournalWritingRequest): boolean;
  /** F-JR-21: prefers a same-id draft over the durable entry. */
  beginEditing(entry: JournalEntry): boolean;
  /** F-JR-21: re-looks up the freshest same-id draft before resuming. */
  resumeDraft(draft: JournalDraft): boolean;
  /** F-JR-40: kind `.goalReflection`, with the goal linked. */
  beginGoalReflection(goal: JournalGoal): boolean;
  /** F-JR-22: resolves recap identity before anything is opened. */
  beginWorkoutRecap(request: JournalRecapRequest): boolean;
  dismiss(): void;
}

/**
 * What an opener decides once the gate has said yes: which seed, and whether a
 * same-id draft already shadows it (F-JR-25's matrix needs the second).
 */
type ComposerSeedPlan =
  | {
      readonly kind: "seed";
      readonly seed: JournalComposerSeed;
      readonly hasSameIdDraft: boolean;
    }
  | { readonly kind: "refused"; readonly message: string };

const JournalComposerSessionContext =
  createContext<JournalComposerSessionState | null>(null);

export interface JournalComposerSessionProviderProps {
  readonly repository: JournalRepositoryPort | null;
  readonly identity: JournalComposerIdentity;
  /** F-JR-24 refuses to finish while media is still importing. */
  readonly isMediaImportInProgress?: () => boolean;
  readonly deleteAttachments?: JournalComposerDependencies["deleteAttachments"];
  readonly children?: ReactNode;
}

/**
 * Structural equality over two records, through the canonical encoder the
 * repository itself verifies writes with.
 *
 * Reusing it is the point: "did anything change" must mean exactly what "would
 * this re-encode to identical bytes" means, or a save that the repository sees
 * as a no-op would still look dirty here — and every close would offer to
 * discard changes the user never made.
 */
function sameRecord(left: JournalEntry, right: JournalEntry): boolean {
  return canonicalJson(left) === canonicalJson(right);
}

export function JournalComposerSessionProvider({
  repository,
  identity,
  isMediaImportInProgress,
  deleteAttachments,
  children,
}: JournalComposerSessionProviderProps): ReactNode {
  const navigation = useNavigation();
  const privacy = usePrivacySession();
  const ports = usePorts();
  const announcer = useOptionalAnnouncer();

  const [controller, setController] = useState<JournalComposerController | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const { presentFullViewport, dismissFullViewport } = navigation;

  /**
   * The latest of everything the openers close over.
   *
   * The `begin…` callbacks are handed to hosts that keep them for the lifetime
   * of a destination (F-NAV-03 keeps all four mounted), so a callback that
   * captured the first render's vault would gate every later attempt on a
   * library state from minutes ago.
   */
  const latest = useRef({
    repository,
    identity,
    isMediaImportInProgress,
    deleteAttachments,
    announcer,
    vault: privacy.vault,
    library: privacy.library,
    clock: ports.clock,
    ticker: ports.ticker,
  });
  latest.current = {
    repository,
    identity,
    isMediaImportInProgress,
    deleteAttachments,
    announcer,
    vault: privacy.vault,
    library: privacy.library,
    clock: ports.clock,
    ticker: ports.ticker,
  };

  const dismiss = useCallback(() => {
    dismissFullViewport();
    setController((current) => {
      current?.dispose();
      return null;
    });
  }, [dismissFullViewport]);

  /**
   * The one gate, and the one place a controller is built.
   *
   * `open` takes a factory rather than a seed so the seed itself is only
   * computed once the gate has said yes — a seed built ahead of the gate would
   * mint an entry id for a composer that never opened, and the summary's recap
   * route would then have burned the session's identity on nothing.
   */
  const open = useCallback(
    (
      makeSeed: (library: JournalOpenLibrary) => ComposerSeedPlan,
    ): boolean => {
      const gate = journalComposerGate(latest.current.vault);
      if (gate.kind === "refused") {
        setRefusal(gate.message);
        return false;
      }

      const library = latest.current.library;
      const repositoryPort = latest.current.repository;
      if (library === null || repositoryPort === null) {
        // A revealed vault with no repository is a build that cannot write at
        // all. It reports the unavailable sentence rather than opening a
        // composer whose every save would fail.
        setRefusal(JOURNAL_LIBRARY_COPY.repositoryUnavailable);
        return false;
      }

      const made = makeSeed(library);
      if (made.kind === "refused") {
        setRefusal(made.message);
        return false;
      }

      const next = new JournalComposerController(made.seed, {
        repository: repositoryPort,
        clock: latest.current.clock,
        ticker: latest.current.ticker,
        identity: latest.current.identity,
        announcer: latest.current.announcer ?? undefined,
        hasSameIdDraft: made.hasSameIdDraft,
        ...(latest.current.isMediaImportInProgress === undefined
          ? {}
          : { isMediaImportInProgress: latest.current.isMediaImportInProgress }),
        ...(latest.current.deleteAttachments === undefined
          ? {}
          : { deleteAttachments: latest.current.deleteAttachments }),
        sameRecord,
      });

      setRefusal(null);
      // Stored before the path is set: the shell mounts the route synchronously
      // from the path, and a route that mounted first would render its empty
      // branch for one commit.
      setController((current) => {
        current?.dispose();
        return next;
      });
      presentFullViewport(PRESENTED_PATH);
      return true;
    },
    [presentFullViewport],
  );

  const beginWriting = useCallback(
    (request: JournalWritingRequest): boolean =>
      open(() => ({
        kind: "seed",
        seed: blankComposerSeed(
          {
            occurredAt: journalInstantFromMilliseconds(latest.current.clock.now()),
            // F-JR-06: a prompt's family decides the kind the entry adopts, so
            // a boxing prompt opens a Training entry without the user choosing.
            ...(request.prompt?.family === undefined
              ? {}
              : { entryKind: JOURNAL_PROMPT_FAMILY_KIND[request.prompt.family] }),
            ...(request.prompt === undefined ? {} : { prompt: request.prompt }),
            ...(request.goal === undefined ? {} : { goal: journalGoalLink(request.goal) }),
          },
          latest.current.identity,
        ),
        hasSameIdDraft: false,
      })),
    [open],
  );

  const beginGoalReflection = useCallback(
    (goal: JournalGoal): boolean =>
      open(() => ({
        kind: "seed",
        seed: blankComposerSeed(
          {
            entryKind: "goalReflection",
            goal: journalGoalLink(goal),
            occurredAt: journalInstantFromMilliseconds(latest.current.clock.now()),
          },
          latest.current.identity,
        ),
        hasSameIdDraft: false,
      })),
    [open],
  );

  const beginEditing = useCallback(
    (entry: JournalEntry): boolean =>
      open((library) => {
        // F-JR-21: the draft is preferred, because it *is* the newer text.
        // Editing the durable revision beside a newer draft would show the user
        // writing they already replaced.
        const draft = freshestDraft(library.projections, library.standaloneDrafts, entry);
        return draft === undefined
          ? { kind: "seed", seed: editingComposerSeed(entry), hasSameIdDraft: false }
          : { kind: "seed", seed: resumingComposerSeed(draft), hasSameIdDraft: true };
      }),
    [open],
  );

  const resumeDraft = useCallback(
    (draft: JournalDraft): boolean =>
      open((library) => {
        // Re-looked up rather than trusted: the caller's copy may be a render
        // old, and resuming a stale draft would autosave over a newer one.
        const fresh =
          freshestDraft(library.projections, library.standaloneDrafts, draft.entry) ?? draft;
        return { kind: "seed", seed: resumingComposerSeed(fresh), hasSameIdDraft: true };
      }),
    [open],
  );

  const beginWorkoutRecap = useCallback(
    (request: JournalRecapRequest): boolean =>
      open((library) => {
        const entries = library.projections.map((projection) => projection.entry);
        const drafts = [
          ...library.standaloneDrafts,
          ...library.projections
            .map((projection) => projection.draft)
            .filter((draft): draft is JournalDraft => draft !== undefined),
        ];
        const resolution = resolveJournalRecap({ request, entries, drafts });
        const refused = recapRefusalMessage(resolution);
        if (refused !== undefined) return { kind: "refused", message: refused };

        switch (resolution.kind) {
          case "resumeDraft":
            return {
              kind: "seed",
              seed: resumingComposerSeed(resolution.draft),
              hasSameIdDraft: true,
            };
          case "editEntry":
            return {
              kind: "seed",
              seed: mergingComposerSeed(request, resolution.entry),
              hasSameIdDraft: false,
            };
          default:
            return {
              kind: "seed",
              seed: workoutRecapComposerSeed(request),
              hasSameIdDraft: false,
            };
        }
      }),
    [open],
  );

  /**
   * Creates the goal, then reports what is durable.
   *
   * The link the sheet applies is built from the **re-decoded** record the
   * repository returns, not from the candidate handed in: a verified write is
   * the only thing that proves the goal the entry is about to point at exists.
   */
  const createGoal = useCallback(async (draft: NewGoalDraft): Promise<JournalGoal | null> => {
    const repositoryPort = latest.current.repository;
    if (repositoryPort === null) return null;
    const now = journalInstantFromMilliseconds(latest.current.clock.now());
    const result = await repositoryPort.saveGoal({
      schemaVersion: JOURNAL_GOAL_SCHEMA_VERSION,
      id: latest.current.identity.nextGoalId(),
      revision: JOURNAL_FIRST_REVISION,
      title: draft.title,
      ...(draft.why === undefined ? {} : { why: draft.why }),
      status: "active",
      ...(draft.targetDate === undefined ? {} : { targetDate: draft.targetDate }),
      createdAt: now,
      updatedAt: now,
    });
    return result.kind === "written" ? result.value : null;
  }, []);

  const value = useMemo<JournalComposerSessionState>(
    () => ({
      controller,
      identity,
      goals: privacy.library?.goals ?? [],
      createGoal,
      refusal,
      beginWriting,
      beginEditing,
      resumeDraft,
      beginGoalReflection,
      beginWorkoutRecap,
      dismiss,
    }),
    [
      beginEditing,
      beginGoalReflection,
      beginWorkoutRecap,
      beginWriting,
      controller,
      createGoal,
      dismiss,
      identity,
      privacy.library,
      refusal,
      resumeDraft,
    ],
  );

  return (
    <JournalComposerSessionContext.Provider value={value}>
      {children}
    </JournalComposerSessionContext.Provider>
  );
}

/**
 * Null rather than a throw, matching the reader's presentation context: a
 * surface with no composer session above it is a surface whose writing action
 * is simply absent — the §8.6 shape — rather than one that crashes on mount.
 */
export function useOptionalJournalComposerSession(): JournalComposerSessionState | null {
  return useContext(JournalComposerSessionContext);
}

/** The library's newest same-id draft, from either place a draft can appear. */
function freshestDraft(
  projections: readonly { readonly draft?: JournalDraft | undefined }[],
  standalone: readonly JournalDraft[],
  entry: JournalEntry,
): JournalDraft | undefined {
  const candidates = [
    ...standalone,
    ...projections
      .map((projection) => projection.draft)
      .filter((draft): draft is JournalDraft => draft !== undefined),
  ].filter((draft) => draft.id === entry.id);

  return candidates.sort((left, right) => right.savedAt - left.savedAt)[0];
}
